import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { ComponentGroup } from '@calendly/ui/components/component-group';
import { SimpleSelect } from '@calendly/ui/components/select';
import { TextInput } from '@calendly/ui/components/text-input';
import classNames from 'classnames';
import { useEffect, useState, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'hooks/useDebounce';
import { Description } from '../styles';
import { createDomainControl } from './actions';
import { ADD_DOMAIN_ERROR_ID, BUTTON_ID, CREATE_DOMAIN_DEBOUNCE_MSEC, DOMAIN_INPUT_ID, SELECT_ID, VERIFICATION_METHODS } from './constants';
import { hasSubdomain } from './second_level_domains/has_subdomain';
import { domainSelector, errorSelector, formDisabledSelector, loadingSelector, submitDisabledSelector, verificationMethodSelector } from './selectors';
import { setDomain as setDomainAction, setError as setErrorAction, setSubmitDisabled, setVerificationMethod as setVerificationMethodAction } from './slice';
import { validDomain } from './utils';
const styledTextInputCss = "soe7ojz";
const StyledTextInput = forwardRef((props, ref) => <TextInput ref={ref} {...props} className={classNames(styledTextInputCss, props.className)} />);
const styledFormCss = "s1lfkbu7";
const StyledForm = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'form';
  return <Component ref={ref} {...restProps} className={classNames(styledFormCss, props.className)} />;
});
const styledSimpleSelectCss = "s72ooi2";
const StyledSimpleSelect = forwardRef((props, ref) => <SimpleSelect ref={ref} {...props} className={classNames(styledSimpleSelectCss, props.className)} />);
const styledErrorCss = "s1w8vpgr";
const StyledError = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'p';
  return <Component ref={ref} {...restProps} className={classNames(styledErrorCss, props.className)} />;
});
const styledMutedMessageCss = "s1lmkgis";
const StyledMutedMessage = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'p';
  return <Component ref={ref} {...restProps} className={classNames(styledMutedMessageCss, props.className)} />;
});
const flexContainerCss = "ff1jxxf";
const FlexContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(flexContainerCss, props.className)} />;
});
const styledButtonCss = "s1tujdl6";
const StyledButton = forwardRef((props, ref) => <Button ref={ref} {...props} className={classNames(styledButtonCss, props.className)} />);
export const CreateDomainControl = () => {
  const dispatch = useDispatch();
  const verificationMethodFromState = useSelector(verificationMethodSelector);
  const domainFromState = useSelector(domainSelector);
  const loadingFromState = useSelector(loadingSelector);
  const formDisabledFromState = useSelector(formDisabledSelector);
  const errorFromState = useSelector(errorSelector);
  const submitDisabledFromState = useSelector(submitDisabledSelector);
  const [domain, setDomain] = useState(domainFromState);
  const [verifyMethodSelectionDisabled, setVerifyMethodSelectionDisabled] = useState(false);
  const debouncedDomain = useDebounce(domain, CREATE_DOMAIN_DEBOUNCE_MSEC);
  useEffect(() => {
    setDomain(domainFromState);
  }, [domainFromState]);
  useEffect(() => {
    if (debouncedDomain.length > 0) {
      if (!validDomain(debouncedDomain)) {
        dispatch(setErrorAction(I18n.t('publisher.organization_settings.domain_control.create.invalid_domain')));
        setVerifyMethodSelectionDisabled(false);
        dispatch(setSubmitDisabled(true));
        return;
      }
      dispatch(setSubmitDisabled(false));
    }
    if (hasSubdomain(debouncedDomain)) {
      dispatch(setVerificationMethodAction(VERIFICATION_METHODS.DNS_TXT));
      setVerifyMethodSelectionDisabled(true);
    } else {
      setVerifyMethodSelectionDisabled(false);
    }
    dispatch(setDomainAction(debouncedDomain));
  }, [dispatch, debouncedDomain]);
  const createDomainControlRecord = event => {
    event.preventDefault();
    dispatch(createDomainControl());
  };
  const setVerificationMethod = verificationMethod => {
    dispatch(setVerificationMethodAction(verificationMethod));
  };
  return <>
      <Description>
        {I18n.t('publisher.organization_settings.domain_control.create.description')}
      </Description>
      <StyledForm onSubmit={event => createDomainControlRecord(event)}>
        <ComponentGroup>
          <FlexContainer>
            <StyledSimpleSelect options={[{
            name: I18n.t('publisher.organization_settings.domain_control.create.methods.txt'),
            value: VERIFICATION_METHODS.DNS_TXT
          }, {
            name: I18n.t('publisher.organization_settings.domain_control.create.methods.cname'),
            value: VERIFICATION_METHODS.DNS_CNAME
          }]} onChange={value => setVerificationMethod(value)} value={verificationMethodFromState} disabled={formDisabledFromState || verifyMethodSelectionDisabled} aria-label={I18n.t('publisher.organization_settings.domain_control.create.verification_method_label')} label={I18n.t('publisher.organization_settings.domain_control.create.verification_method_label')} id={SELECT_ID} />
            <StyledTextInput placeholder={I18n.t('publisher.organization_settings.domain_control.create.domain_placeholder')} onChange={event => setDomain(event.target.value)} value={domain} disabled={formDisabledFromState} id={DOMAIN_INPUT_ID} />
          </FlexContainer>
        </ComponentGroup>
        <StyledButton type="submit" loading={loadingFromState} disabled={formDisabledFromState || submitDisabledFromState || domain !== debouncedDomain || loadingFromState} id={BUTTON_ID}>
          {I18n.t('publisher.organization_settings.domain_control.create.button')}
        </StyledButton>
      </StyledForm>
      {errorFromState && <StyledError id={ADD_DOMAIN_ERROR_ID}>{errorFromState}</StyledError>}
      {verifyMethodSelectionDisabled && <StyledMutedMessage>
          {I18n.t('publisher.organization_settings.domain_control.create.subdomain_message')}
        </StyledMutedMessage>}
    </>;
};

require("./create_domain_control.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./create_domain_control.js");